import React from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import ExportDownloadButtons from './ExportDownloadButtons';
import { formatBytes } from './exportFormat';
import { getCurrentTask, getDownloadParts, getTotalBytes, isExportActive, isExportDownloadable, parseExportDateTime } from './exportJob';

const TEXT_SX = { color: 'var(--color-text-dark)', lineHeight: 1.5 };
const LIGHT_TEXT_SX = { color: 'var(--color-text-light)', lineHeight: 1.5 };
const FULL_WIDTH_ALERT_SX = { width: '100%', boxSizing: 'border-box' };

const formatDateTime = (value) => {
    const date = parseExportDateTime(value);
    return date ? date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '';
};

/**
 * Situação de uma exportação completa, em poucas linhas: em andamento
 * (progresso e etapa), concluída (download) ou com erro (motivo).
 * `starting` cobre o instante entre o clique e o job aparecer no histórico;
 * `autoDownload` diz que o job foi criado nesta tela e a página baixa o
 * arquivo sozinha ao terminar (os textos avisam). `onDownload` e
 * `downloadedIndexes` vão para os botões de download (ver
 * ExportDownloadButtons).
 * Sem job relevante e sem `starting`, não renderiza nada.
 * Usado no passo Exportar do diálogo "Encerrar conta".
 */
function ExportJobStatus({ job, starting = false, autoDownload = false, onDownload, downloadedIndexes }) {
    if (starting || isExportActive(job)) {
        const progress = starting ? 0 : Number(job?.progress) || 0;
        const task = starting ? 'Iniciando a exportação' : (getCurrentTask(job) || 'Preparando os dados');

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CircularProgress
                        variant={progress > 0 ? 'determinate' : 'indeterminate'}
                        value={progress}
                        size={28}
                    />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ ...TEXT_SX, fontWeight: 600 }}>
                            {`Exportação em andamento: ${progress}%`}
                        </Typography>
                        <Typography variant="caption" sx={{ ...LIGHT_TEXT_SX, display: 'block' }}>
                            {task}
                        </Typography>
                    </Box>
                </Box>
                <Typography variant="body2" sx={LIGHT_TEXT_SX}>
                    {autoDownload
                        ? 'Pode levar alguns minutos. Se você continuar nesta tela, o download começa sozinho ao terminar; o arquivo também fica no histórico desta página por 24 horas.'
                        : 'O arquivo é gerado em segundo plano e pode levar alguns minutos. Ao terminar, o download fica no histórico desta página por 24 horas.'}
                </Typography>
            </Box>
        );
    }

    if (isExportDownloadable(job)) {
        const when = formatDateTime(job.completed_at);
        const size = getTotalBytes(job);
        const multipart = getDownloadParts(job).length > 1;
        let downloadNote = '';
        if (autoDownload) {
            downloadNote = multipart
                ? 'A parte 1 começou a baixar automaticamente; baixe as demais pelos botões. '
                : 'O download começou automaticamente; se o navegador não baixou, use o botão. ';
        }

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                <Alert severity="success" sx={FULL_WIDTH_ALERT_SX}>
                    {`Exportação concluída${when ? ` em ${when}` : ''}${size ? ` (${formatBytes(size)})` : ''}. `}
                    {downloadNote}
                    Guarde o arquivo em local seguro.
                </Alert>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <ExportDownloadButtons item={job} showLabel onDownload={onDownload} downloadedIndexes={downloadedIndexes} />
                </Box>
            </Box>
        );
    }

    if (job?.status === 'error') {
        const reason = (getCurrentTask(job) || '').replace(/\.$/, '');

        return (
            <Alert severity="error" sx={FULL_WIDTH_ALERT_SX}>
                {`A última exportação falhou${reason ? `: ${reason}` : ''}. Tente novamente.`}
            </Alert>
        );
    }

    return null;
}

export default ExportJobStatus;
